import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CalendarCheck, 
  ArrowLeft, 
  Phone, 
  Mail,
  MapPin, 
  MessageSquare,
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Call Us",
      content: "(877) 739-2487",
      description: "Mon-Fri, 9am-6pm EST"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email Us",
      content: "support@renewalnote.com",
      description: "We respond within 24 hours"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Office",
      content: "OFFER GADGET LLC",
      description: "53 Calle Palmeras Ste 601, San Juan, PR 00901"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Business Hours",
      content: "Monday - Friday",
      description: "9:00 AM - 6:00 PM EST"
    }
  ];

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-12 md:py-16 bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-ink tracking-tight font-display mb-4">
            Get in touch.
          </h1>
          <p className="text-lg text-ink-600 max-w-2xl mx-auto">
            Got a question or stuck on something? Drop us a line below or reach out directly — a real human will reply within a business day.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border border-ink-200 shadow-sm">
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-ink font-display mb-2">
                        Thanks — we got it.
                      </h2>
                      <p className="text-ink-600 mb-6">
                        Your message landed. We'll be back to you within one business day.
                      </p>
                      <Button 
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: '', email: '', subject: '', category: '', message: '' });
                        }}
                        variant="outline"
                        className="hover:bg-cream hover:text-ink"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <MessageSquare className="w-6 h-6 text-respondent" />
                        <h2 className="text-xl font-bold text-ink font-display">Send us a Message</h2>
                      </div>
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-ink">Full Name *</Label>
                            <Input
                              id="name"
                              placeholder="John Doe"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              required
                              className="border-ink-200 focus:ring-2 focus:ring-ink focus:border-transparent"
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-ink">Email Address *</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="john@example.com"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              required
                              className="border-ink-200 focus:ring-2 focus:ring-ink focus:border-transparent"
                              data-testid="contact-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="category" className="text-ink">Category *</Label>
                            <Select 
                              value={formData.category} 
                              onValueChange={(value) => setFormData({ ...formData, category: value })}
                              required
                            >
                              <SelectTrigger className="border-ink-200" data-testid="contact-category-select">
                                <SelectValue placeholder="Select a category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">General Inquiry</SelectItem>
                                <SelectItem value="support">Technical Support</SelectItem>
                                <SelectItem value="billing">Billing & Payments</SelectItem>
                                <SelectItem value="feature">Feature Request</SelectItem>
                                <SelectItem value="bug">Report a Bug</SelectItem>
                                <SelectItem value="partnership">Partnership</SelectItem>
                                <SelectItem value="press">Press & Media</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subject" className="text-ink">Subject *</Label>
                            <Input
                              id="subject"
                              placeholder="How can we help?"
                              value={formData.subject}
                              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                              required
                              className="border-ink-200 focus:ring-2 focus:ring-ink focus:border-transparent"
                              data-testid="contact-subject-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-ink">Message *</Label>
                          <Textarea
                            id="message"
                            placeholder="Please describe your question or concern in detail..."
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            required
                            rows={6}
                            className="border-ink-200 focus:ring-2 focus:ring-ink focus:border-transparent resize-none"
                            data-testid="contact-message-input"
                          />
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <p className="text-sm text-ink-600">* Required fields</p>
                          <Button 
                            type="submit" 
                            className="bg-ink hover:bg-black text-white px-6"
                            disabled={loading || !formData.category}
                            data-testid="contact-submit-btn"
                          >
                            {loading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Message
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <Card key={index} className="bg-white border border-ink-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-md bg-cream flex items-center justify-center text-respondent flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-ink">{info.title}</h3>
                        <p className="text-ink font-medium">{info.content}</p>
                        <p className="text-sm text-ink-600">{info.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* FAQ Link */}
              <Card className="bg-cream border border-ink-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-ink mb-2">Looking for a quick answer?</h3>
                  <p className="text-sm text-respondent mb-3">
                    Most questions about renewals, reminders, and billing are already covered in our help guide.
                  </p>
                  <Link to="/resources">
                    <Button variant="outline" size="sm" className="border-ink-200 text-respondent hover:bg-cream hover:text-respondent">
                      Browse the help guide
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
